// MCQMultipleQuestionPremium.tsx - World-Class Premium Multiple Choice Component
// Features enhanced UI/UX with premium design elements, animations, and visual hierarchy
/* eslint-disable @typescript-eslint/no-explicit-any */

import { LinearGradient } from 'expo-linear-gradient';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, ScrollView } from 'react-native';
import ReAnimated from 'react-native-reanimated';

import { useFadeIn, useSpringPress, useCorrectPulse, useWrongShake } from '../../../design-system/animations';
import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius, shadows, animation } from '../../../design-system/tokens';
import { useLanguageStore } from '../../../features/quiz-core/languageStore';
import { t } from '../../../i18n/translations';
import { IQESAnswerOption, IQESSceneBackdrop } from '../../../shared/ui/IQESComponents';
import { iqesColors, iqesTypography } from '../../../shared/ui/iqesDesignSystem';
import { inferIQESSceneKey } from '../../../shared/ui/iqesSceneSystem';
import DevotionalExplanation from '../DevotionalExplanation';

import { MCQMultipleQuestionProps, MCQMultipleAnswer } from './types/QuestionFormatProps';

/* ── AnimatedOption: wraps each option with reanimated spring / pulse / shake ── */
interface AnimatedOptionProps {
  children: React.ReactNode;
  isCorrectReview: boolean;   // true when showCorrectAnswer && option.correct
  isWrongReview: boolean;     // true when showCorrectAnswer && selected && !correct
  disabled: boolean;
  style?: any;
  existingAnimStyle?: any;    // existing RN Animated transform
}

const AnimatedOption: React.FC<AnimatedOptionProps> = React.memo(({
  children,
  isCorrectReview,
  isWrongReview,
  disabled,
  style,
  existingAnimStyle,
}) => {
  const { animatedStyle: springStyle, onPressIn, onPressOut } = useSpringPress(0.97);
  const { animatedStyle: correctStyle } = useCorrectPulse(isCorrectReview);
  const wrongStyle = useWrongShake(isWrongReview);

  return (
    <Animated.View style={[style, existingAnimStyle]}>
      <ReAnimated.View style={[
        springStyle,
        isCorrectReview && correctStyle,
        isWrongReview && wrongStyle,
      ]}>
        {React.Children.map(children, (child) => React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, {
            onPressIn: () => {
              if (!disabled) onPressIn();
              (child.props as any).onPressIn?.();
            },
            onPressOut: () => {
              onPressOut();
              (child.props as any).onPressOut?.();
            },
          })
          : child)}
      </ReAnimated.View>
    </Animated.View>
  );
});

const MCQMultipleQuestionPremium: React.FC<MCQMultipleQuestionProps> = ({
  question,
  currentIndex: _currentIndex,
  totalQuestions: _totalQuestions,
  onAnswer,
  selectedAnswers = [],
  explanation,
  disabled = false,
  showCorrectAnswer = false,
  highlightCorrect: _highlightCorrect = false,
  showExplanation = false,
  timeSpent = 0,
  isAnswered = false,
  selectionStyle: _selectionStyle = 'premium',
  showSelectionCounter = true,
  allowPartialSubmit: _allowPartialSubmit = false,
  maxSelectionsWarning = true,
  animationDuration = animation.normal,
  hapticFeedback = true,
  theme: _theme = 'premium',
  showProgressIndicator: _showProgressIndicator = true,
  accessibilityLabel: _accessibilityLabel,
  accessibilityHint: _accessibilityHint
}) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const styles = createStyles(colors);
  // DS entrance animation for the question card
  const fadeInStyle = useFadeIn();

  // Ensure all options have IDs before anything else
  const optionsWithIds = React.useMemo(() => {
    return question.format_data.options.map((opt: any, index: number) => {
      if (opt.id === undefined) {
        return { ...opt, id: index + 1 };
      }
      return opt;
    });
  }, [question.format_data.options]);

  // Update format_data with options that have IDs
  const enhancedQuestion = React.useMemo(() => ({
    ...question,
    format_data: {
      ...question.format_data,
      options: optionsWithIds
    }
  }), [question, optionsWithIds]);

  const [localSelectedAnswers, setLocalSelectedAnswers] = useState<(string | number)[]>(selectedAnswers);
  const [forceRender, setForceRender] = useState(0); // Force re-render trigger
  const animatedValuesRef = useRef<Map<string | number, any>>(new Map());
  // Initialize with -1 so the first question always triggers the reset logic
  const previousQuestionId = useRef<number>(-1);

  // Helper function for type-safe option ID comparison - handle both strings and numbers
  const isOptionSelected = useCallback((optionId: any, selectedAnswers: any[]) => {
    return selectedAnswers.some(selectedId => {
      // Direct comparison first (most common case)
      if (selectedId === optionId) return true;

      // Try string comparison
      if (String(selectedId) === String(optionId)) return true;

      // Try number comparison if both can be converted
      const numericSelectedId = typeof selectedId === 'string' ? parseInt(selectedId) : selectedId;
      const numericOptionId = typeof optionId === 'string' ? parseInt(optionId) : optionId;
      if (!isNaN(numericSelectedId) && !isNaN(numericOptionId) && numericSelectedId === numericOptionId) return true;

      return false;
    });
  }, []);

  // Update animated values based on current selections (separate from initialization)
  useEffect(() => {
    // Only update if we have animated values already created
    if (animatedValuesRef.current.size > 0) {
      enhancedQuestion.format_data.options.forEach((option, index) => {
        const optionId = option.id !== undefined ? option.id : index + 1;
        const existingValues = animatedValuesRef.current.get(optionId);
        if (existingValues) {
          // Update checkbox scale based on current selection state
          const shouldBeSelected = isOptionSelected(optionId, localSelectedAnswers);
          existingValues.checkboxScale.setValue(shouldBeSelected ? 1 : 0);
        }
      });
    }
  }, [localSelectedAnswers, enhancedQuestion.format_data.options, isOptionSelected, enhancedQuestion.id, forceRender]);

  const formatData = enhancedQuestion.format_data;
  const isScreenshotSmokeQuestion = Number(enhancedQuestion.id) >= 9100 && Number(enhancedQuestion.id) < 9200;
  const screenshotPhase = (formatData as MCQMultipleFormatData & { screenshot_phase?: string }).screenshot_phase;
  const shouldShowSmokeSelections = isScreenshotSmokeQuestion
    && (screenshotPhase === 'selected' || screenshotPhase === 'locked' || showCorrectAnswer);
  const minSelections = formatData.min_selections || 1;
  const maxSelections = formatData.max_selections || formatData.options.length;
  const correctAnswerCount = formatData.options.filter((opt: any) => opt.correct).length;
  const screenshotSelectedAnswers = React.useMemo(
    () => shouldShowSmokeSelections
      ? enhancedQuestion.format_data.options
        .filter((option: any) => option.correct)
        .map((option: any, index: number) => option.id ?? index + 1)
      : [],
    [enhancedQuestion.format_data.options, shouldShowSmokeSelections]
  );
  const visibleSelectedAnswers = shouldShowSmokeSelections && !showCorrectAnswer
    ? screenshotSelectedAnswers
    : localSelectedAnswers;
  const canSubmit = localSelectedAnswers.length === correctAnswerCount; // Only submit when exact number selected
  const replaceTemplate = useCallback((template: string, values: Record<string, string | number>) => {
    return Object.entries(values).reduce(
      (output, [key, value]) => output.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
      template
    );
  }, []);
  const selectedCountLabel = replaceTemplate(
    t('quizFlow.selectionCount', language) || '{{selected}} of {{total}} selected',
    {
      selected: visibleSelectedAnswers.length,
      total: formatData.options.length,
    }
  );

  // Enhanced contextual instruction generator - shows exact number of correct answers
  const getContextualInstruction = (min: number, max: number) => {
    const actualCorrectCount = formatData.options.filter((opt: any) => opt.correct).length;

    // ALWAYS show the exact number of correct answers for maximum clarity
    // This removes all ambiguity and tells users exactly what they need to do
    if (actualCorrectCount > 0) {
      if (actualCorrectCount === 1) {
        return t('quizFlow.selectOne', language) || 'Select 1 answer';
      }

      return replaceTemplate(
        t('quizFlow.selectMany', language) || 'Select {{count}} answers',
        { count: actualCorrectCount }
      );
    }

    // Fallback if we can't determine correct count (shouldn't happen with proper data)
    console.warn('[MCQMultiple] Could not determine correct answer count, falling back to range');
    if (min === max) {
      if (min === 1) {
        return t('quizFlow.selectOne', language) || 'Select 1 answer';
      }

      return replaceTemplate(
        t('quizFlow.selectMany', language) || 'Select {{count}} answers',
        { count: min }
      );
    }

    return replaceTemplate(
      t('quizFlow.selectRange', language) || 'Select {{min}}-{{max}} answers',
      { min, max }
    );
  };

  // Update local state when question changes (not when selections change)
  useEffect(() => {
    // Only reset if this is actually a new question
    if (previousQuestionId.current !== enhancedQuestion.id) {
      // Clear state completely and force reset
      // Only apply prop selections if we're in review mode (showCorrectAnswer is true)
      // Otherwise, always start fresh for new questions
      const initialSelections = showCorrectAnswer && selectedAnswers
        ? selectedAnswers
        : shouldShowSmokeSelections
          ? enhancedQuestion.format_data.options
            .filter((option: any) => option.correct)
            .map((option: any, index: number) => option.id ?? index + 1)
          : [];
      setLocalSelectedAnswers([...initialSelections]);
      setForceRender(prev => prev + 1); // Force re-render immediately
      previousQuestionId.current = enhancedQuestion.id;

      // Force complete re-initialization of animated values for the new question
      const newAnimatedValues = new Map();
      enhancedQuestion.format_data.options.forEach((option, index) => {
        const optionId = option.id !== undefined ? option.id : index + 1;
        // Initialize checkbox scale based on whether this option should be selected
        const shouldBeSelected = initialSelections.includes(optionId);
        newAnimatedValues.set(optionId, {
          scale: new Animated.Value(1),
          opacity: new Animated.Value(1),
          checkboxScale: new Animated.Value(shouldBeSelected ? 1 : 0),
          shimmer: new Animated.Value(0),
          bounce: new Animated.Value(0)
        });
      });
      animatedValuesRef.current = newAnimatedValues;
    }
  }, [enhancedQuestion.id, selectedAnswers, formatData.options, showCorrectAnswer, shouldShowSmokeSelections, enhancedQuestion.format_data.options]);

  // Premium animation helpers
  const animateOption = useCallback((optionId: string | number, selected: boolean, pressed: boolean = false) => {
    const values = animatedValuesRef.current.get(optionId);
    if (!values) return;

    if (pressed) {
      // Enhanced button press animation with bounce
      Animated.sequence([
        Animated.parallel([
          Animated.timing(values.scale, {
            toValue: 0.96,
            duration: animationDuration / 3,
            useNativeDriver: true
          }),
          Animated.timing(values.bounce, {
            toValue: 1,
            duration: animationDuration / 3,
            useNativeDriver: true
          })
        ]),
        Animated.parallel([
          Animated.spring(values.scale, {
            toValue: selected ? 1.02 : 1,
            useNativeDriver: true,
            speed: 20,
            bounciness: 8
          }),
          Animated.timing(values.bounce, {
            toValue: 0,
            duration: animationDuration / 2,
            useNativeDriver: true
          })
        ])
      ]).start();
    }

    // Premium checkbox animation with spring
    Animated.spring(values.checkboxScale, {
      toValue: selected ? 1 : 0,
      useNativeDriver: true,
      speed: 15,
      bounciness: 12
    }).start();

    // Shimmer effect on selection
    if (selected) {
      Animated.sequence([
        Animated.timing(values.shimmer, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true
        }),
        Animated.timing(values.shimmer, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true
        })
      ]).start();
    }
  }, [animationDuration]);

  // Handle option selection
  const handleOptionSelect = useCallback((optionId: string | number) => {
    if (disabled || isAnswered) return;

    // Use helper function for type-safe comparison
    const isCurrentlySelected = isOptionSelected(optionId, localSelectedAnswers);

    let newSelectedAnswers: (string | number)[];

    if (isCurrentlySelected) {
      // Remove this option from selection using type-safe comparison
      newSelectedAnswers = localSelectedAnswers.filter(selectedId =>
        !isOptionSelected(optionId, [selectedId])
      );
    } else {
      // Prevent selecting more than the correct number of answers
      if (localSelectedAnswers.length >= correctAnswerCount) {
        if (maxSelectionsWarning && hapticFeedback && typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate([100, 50, 100]);
        }
        return;
      }
      // Keep the original data type of optionId (string or number)
      newSelectedAnswers = [...localSelectedAnswers, optionId];
    }

    setLocalSelectedAnswers(newSelectedAnswers);

    // Force component re-render to ensure visual updates
    setForceRender(prev => prev + 1);

    // Force a re-render by updating the animated value immediately
    const values = animatedValuesRef.current.get(optionId);
    if (values) {
      // Immediately update the checkbox scale value before animation
      if (!isCurrentlySelected) {
        // Show selection
        values.checkboxScale.setValue(1);
        Animated.timing(values.checkboxScale, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true
        }).start();
      } else {
        // Hide deselection
        values.checkboxScale.setValue(0);
        Animated.timing(values.checkboxScale, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true
        }).start();
      }
    }

    animateOption(optionId, !isCurrentlySelected, true);

    if (hapticFeedback && typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(40);
    }

  }, [disabled, isAnswered, localSelectedAnswers, correctAnswerCount, minSelections, hapticFeedback, maxSelectionsWarning, animateOption, isOptionSelected]);

  // Submit answer function
  const submitAnswer = useCallback((answers: (string | number)[] = localSelectedAnswers) => {
    if (disabled || isAnswered || answers.length !== correctAnswerCount) return;

    // Convert answers to consistent format for submission
    const submissionAnswers = answers.map(answer => {
      // If it's a string that can be converted to number, convert it
      if (typeof answer === 'string') {
        const numericValue = parseInt(answer);
        return !isNaN(numericValue) ? numericValue : answer;
      }
      return answer;
    }) as number[];

    const mcqMultipleAnswer: MCQMultipleAnswer = {
      format: 'mcq_multiple',
      answers: submissionAnswers,
      confidence_level: 3,
      time_spent_seconds: timeSpent
    };

    onAnswer(mcqMultipleAnswer);
  }, [disabled, isAnswered, correctAnswerCount, onAnswer, timeSpent, localSelectedAnswers]);

  const getOptionState = useCallback((option: any): 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled' => {
    const isSelected = isOptionSelected(option.id, visibleSelectedAnswers);
    const isCorrect = showCorrectAnswer && option.correct;
    const isIncorrect = showCorrectAnswer && isSelected && !option.correct;

    if (isIncorrect) return 'incorrect';
    if (isCorrect) return 'correct';
    if (isSelected) return 'selected';
    if (disabled && !isAnswered) return 'disabled';
    return 'default';
  }, [disabled, isAnswered, isOptionSelected, visibleSelectedAnswers, showCorrectAnswer, forceRender]);

  const submitButtonLabel = canSubmit
    ? (t('quizFlow.submit', language) || 'Submit')
    : correctAnswerCount - localSelectedAnswers.length === 1
      ? (t('quizFlow.selectOne', language) || 'Select 1 answer')
      : replaceTemplate(
          t('quizFlow.selectMany', language) || 'Select {{count}} answers',
          { count: Math.max(correctAnswerCount - localSelectedAnswers.length, 0) }
        );
  const sceneKey = inferIQESSceneKey({
    question: enhancedQuestion.question,
    questionType: 'mcq_multiple',
  });
  const referenceText = String(formatData.reference || formatData.verse_reference || '');

  return (
    <ScrollView testID="mcq-multiple-screen" style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Question Card - matching MCQ Single design */}
      <ReAnimated.View style={fadeInStyle}>
        <IQESSceneBackdrop sceneKey={sceneKey} style={styles.questionCard}>
          <View style={styles.questionScrim}>
            <DSText variant="title" color={iqesColors.ivory} align="center" style={styles.questionText}>
              {enhancedQuestion.question}
            </DSText>
            {!!referenceText && (
              <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.referenceText}>
                {referenceText}
              </DSText>
            )}
          </View>
        </IQESSceneBackdrop>
      </ReAnimated.View>

      {/* Enhanced Selection Instructions - contextual and specific */}
      {!isScreenshotSmokeQuestion && (
        <View style={styles.instructionContainer}>
          <DSText variant="label" color={colors.primary} style={styles.instructionText}>
            {getContextualInstruction(minSelections, maxSelections, formatData.options.filter((opt: any) => opt.correct).length)}
          </DSText>

          {showSelectionCounter && (
            <View style={[
              styles.selectionCounterBadge,
              visibleSelectedAnswers.length < formatData.options.filter((opt: any) => opt.correct).length && styles.selectionCounterWarning,
              visibleSelectedAnswers.length === formatData.options.filter((opt: any) => opt.correct).length && styles.selectionCounterSuccess,
              visibleSelectedAnswers.length > formatData.options.filter((opt: any) => opt.correct).length && styles.selectionCounterMax
            ]}>
              <DSText variant="label" color={colors.onSurface} style={styles.selectionCounterText}>
                {visibleSelectedAnswers.length} / {formatData.options.filter((opt: any) => opt.correct).length}
              </DSText>
            </View>
          )}
        </View>
      )}

      {/* Premium Options List */}
      <View style={styles.optionsContainer}>
        {formatData.options.map((option, index) => {
          // Use index as fallback if option.id is not defined
          const optionId = option.id !== undefined ? option.id : index + 1;

          const animatedValue = animatedValuesRef.current.get(optionId);
          // Use helper function for type-safe comparison
          const isSelected = isOptionSelected(optionId, visibleSelectedAnswers);
          const optionLetter = String.fromCharCode(65 + index);

          const isCorrectReview = showCorrectAnswer && !!option.correct;
          const isWrongReview = showCorrectAnswer && isSelected && !option.correct;
          const reviewState = showCorrectAnswer
            ? isSelected && option.correct
              ? 'selected-correct'
              : isSelected && !option.correct
                ? 'selected-wrong'
                : option.correct
                  ? 'missed-correct'
                  : null
            : null;
          const reviewBadgeLabel = reviewState === 'selected-correct'
            ? (t('quizFlow.selectedCorrect', language) || 'Affirmed')
            : reviewState === 'selected-wrong'
              ? (t('quizFlow.selectedWrong', language) || 'Your choice')
              : reviewState === 'missed-correct'
                ? (t('quizFlow.missedCorrect', language) || 'Missed')
                : null;
          const selectionAccessibility = reviewBadgeLabel || (isSelected
            ? t('quizFlow.optionSelectedAccessibility', language)
            : t('quizFlow.optionNotSelectedAccessibility', language));
          const optionAccessibilityLabel = replaceTemplate(
            t('quizFlow.optionAccessibility', language),
            {
              option: optionLetter,
              text: String(option.text || ''),
              status: selectionAccessibility,
            }
          );

          return (
            <AnimatedOption
              key={`mcq-multiple-option-${optionId}-${index}`}
              isCorrectReview={isCorrectReview}
              isWrongReview={isWrongReview}
              disabled={disabled || isAnswered}
              style={styles.optionWrapper}
              existingAnimStyle={animatedValue && {
                transform: [{ scale: animatedValue.scale }]
              }}
            >
              <IQESAnswerOption
                label={String(option.text || '')}
                index={index}
                state={getOptionState({ ...option, id: optionId })}
                trailingIcon={isSelected || (showCorrectAnswer && option.correct) ? 'checkmark' : undefined}
                style={styles.multiSelectOption}
                onPress={() => handleOptionSelect(optionId)}
                disabled={disabled || isAnswered}
                accessibilityLabel={optionAccessibilityLabel}
              />
            </AnimatedOption>
          );
        })}
      </View>

      {isScreenshotSmokeQuestion && (
        <DSText variant="caption" color={iqesColors.goldSoft} align="center" style={styles.screenshotSelectionSummary}>
          {selectedCountLabel}
        </DSText>
      )}

      {/* Premium Submit Button */}
      {!isAnswered && canSubmit && !isScreenshotSmokeQuestion && (
        <LinearGradient
          colors={canSubmit ? colors.gradientPrimary : [colors.outline + '66', colors.outline + '33']}
          style={styles.submitButton}
        >
          <TouchableOpacity
            style={styles.submitButtonContent}
            onPress={() => submitAnswer()}
            disabled={!canSubmit || disabled}
            accessibilityLabel={submitButtonLabel}
          >
            <DSText
              variant="title"
              color={canSubmit ? colors.white : colors.outline + 'CC'}
              style={styles.submitButtonText}
            >
              {submitButtonLabel}
            </DSText>
          </TouchableOpacity>
        </LinearGradient>
      )}

      {/* Devotional Explanation */}
      {(showCorrectAnswer || showExplanation) && explanation && (
        <DevotionalExplanation explanation={explanation} />
      )}
    </ScrollView>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
  },


  // Question Card - matching MCQ Single design exactly
  questionCard: {
    backgroundColor: 'rgba(10, 14, 32, 0.78)',
    borderRadius: radius.lg,
    marginBottom: spacing.xl,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.36)',
    ...shadows.md,
    overflow: 'hidden',
    minHeight: 174,
    justifyContent: 'center',
  },
  questionScrim: {
    flex: 1,
    minHeight: 174,
    justifyContent: 'center',
    padding: spacing['2xl'],
    backgroundColor: 'rgba(3, 5, 16, 0.34)',
  },
  questionText: {
    fontFamily: iqesTypography.displayFamily,
    fontWeight: '800',
    lineHeight: 31,
  },
  referenceText: {
    marginTop: spacing.sm,
    fontWeight: '700',
  },

  // Selection Instructions - subtle card design
  instructionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.xl,
    paddingVertical: 14,
    backgroundColor: 'rgba(41, 25, 8, 0.72)',
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.38)',
    ...shadows.sm,
  },
  instructionText: {
    fontWeight: '600',
    letterSpacing: 0.2,
    color: iqesColors.goldSoft,
  },
  selectionCounterBadge: {
    backgroundColor: 'rgba(3, 5, 16, 0.66)',
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.34)',
    ...shadows.sm,
  },
  selectionCounterWarning: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  selectionCounterMax: {
    backgroundColor: colors.incorrect,
    borderColor: colors.incorrect,
  },
  selectionCounterSuccess: {
    backgroundColor: colors.correct,
    borderColor: colors.correct,
  },
  selectionCounterText: {
    fontSize: 13,
    fontWeight: '700',
  },

  // Premium Options Container
  optionsContainer: {
    marginBottom: spacing.xl,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: spacing.sm,
    rowGap: spacing.md,
  },
  optionWrapper: {
    width: '48.5%',
  },
  multiSelectOption: {
    minHeight: 58,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 14,
  },
  screenshotSelectionSummary: {
    marginTop: -spacing.md,
    marginBottom: spacing.md,
    fontWeight: '700',
  },

  // Premium Option Styles with High Contrast
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.xl,
    paddingVertical: 18,
    paddingHorizontal: 18,
    marginHorizontal: spacing.xs,
    marginVertical: 6,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.1,
    shadowRadius: 14,
    elevation: 8,
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    position: 'relative',
    overflow: 'hidden',
  },
  selectedOption: {
    backgroundColor: colors.primaryContainer,
    borderColor: colors.primary,
    borderWidth: 2,
    shadowColor: colors.primary,
    shadowOpacity: 0.2,
    transform: [{ scale: 1.01 }],
  },
  correctOption: {
    backgroundColor: colors.correct,
    borderColor: colors.correct,
    borderWidth: 2,
    shadowColor: colors.correct,
    shadowOpacity: 0.25,
  },
  incorrectOption: {
    backgroundColor: colors.incorrect,
    borderColor: colors.incorrect,
    borderWidth: 2,
    shadowColor: colors.incorrect,
    shadowOpacity: 0.25,
  },
  missedOption: {
    backgroundColor: colors.accentContainer,
    borderColor: colors.accent,
    borderWidth: 2,
    shadowColor: colors.accent,
    shadowOpacity: 0.18,
  },
  disabledOption: {
    backgroundColor: colors.surfaceContainerHigh,
    borderColor: colors.surfaceContainerHigh,
    shadowOpacity: 0,
    elevation: 2,
    opacity: 0.6,
  },

  // Premium Option Badge with Better Contrast
  optionBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.lg,
    ...shadows.sm,
  },
  optionBadgeSelected: {
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.primary,
    shadowOpacity: 0.2,
    elevation: 4,
  },
  optionBadgeText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  optionBadgeDefault: {
    backgroundColor: colors.surfaceContainerHighest,
    borderWidth: 2,
    borderColor: colors.outlineVariant,
  },
  optionBadgeCorrect: {
    backgroundColor: colors.correct,
    borderWidth: 2,
    borderColor: colors.correct,
  },
  optionBadgeIncorrect: {
    backgroundColor: colors.incorrect,
    borderWidth: 2,
    borderColor: colors.incorrect,
  },
  optionBadgeMissed: {
    backgroundColor: colors.accent,
    borderWidth: 2,
    borderColor: colors.accent,
  },

  // Option Content
  optionContent: {
    flex: 1,
    paddingRight: spacing.lg,
  },
  optionText: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.onSurface,
    lineHeight: 22,
    letterSpacing: 0.1,
  },
  selectedOptionText: {
    color: colors.onPrimaryContainer,
    fontWeight: '700',
  },
  correctOptionText: {
    color: colors.onCorrect,
    fontWeight: '700',
  },
  incorrectOptionText: {
    color: colors.onIncorrect,
    fontWeight: '700',
  },
  missedOptionText: {
    color: colors.onAccentContainer,
    fontWeight: '700',
  },
  disabledOptionText: {
    color: colors.outline,
  },

  // Simple Selection Indicator
  indicatorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: spacing['3xl'],
    height: spacing['3xl'],
  },
  selectionRing: {
    width: spacing['2xl'],
    height: spacing['2xl'],
    borderRadius: radius.md,
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    backgroundColor: colors.surfaceContainerHighest,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionRingSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryContainer,
  },
  selectionRingCorrect: {
    borderColor: colors.correct,
    backgroundColor: colors.correct,
  },
  selectionRingIncorrect: {
    borderColor: colors.incorrect,
    backgroundColor: colors.incorrect,
  },
  selectionRingMissed: {
    borderColor: colors.accent,
    backgroundColor: colors.accentContainer,
  },
  statusIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusIconText: {
    fontSize: 14,
    fontWeight: '800',
  },

  // Premium Submit Button
  submitButton: {
    borderRadius: radius.xl,
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
    ...shadows.lg,
  },
  submitButtonContent: {
    paddingVertical: 18,
    paddingHorizontal: spacing['3xl'],
    alignItems: 'center',
  },
  submitButtonText: {
    fontWeight: '800',
    letterSpacing: 0.5,
  },

  // Result indicator styles
  correctIcon: {
    backgroundColor: colors.correct,
  },
  incorrectIcon: {
    backgroundColor: colors.incorrect,
  },
  correctIconText: {
    color: colors.onCorrect,
  },
  incorrectIconText: {
    color: colors.onIncorrect,
  },
  reviewBadge: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  reviewBadgeCorrect: {
    backgroundColor: colors.onCorrect + '1A',
  },
  reviewBadgeIncorrect: {
    backgroundColor: colors.onIncorrect + '1A',
  },
  reviewBadgeMissed: {
    backgroundColor: colors.onAccentContainer + '14',
  },
  reviewBadgeText: {
    fontWeight: '800',
    letterSpacing: 0.5,
  },

});

export default MCQMultipleQuestionPremium;
